import { useTranslation } from '@/i18n';
import { Icons } from '@/utils/Icons';
import type { WithdrawAccount } from './withdrawAccounts';

type Props = {
  accounts: WithdrawAccount[];
  isLoading: boolean;
  isError: boolean;
  onSelect: (account: WithdrawAccount) => void;
  onAddAccount: () => void;
};

const ACCOUNT_ICONS = {
  bank: { icon: Icons.bank, className: 'bg-gold/15 text-gold' },
  upi: { icon: Icons.qr, className: 'bg-shell text-shell-fg' },
  crypto: { icon: Icons.tether, className: 'bg-success-emerald text-body-inverse' },
};

export const WithdrawAccountIcon = ({ kind }: { kind: WithdrawAccount['kind'] }) => {
  const { icon: Icon, className } = ACCOUNT_ICONS[kind];
  return (
    <span
      className={`flex size-12 shrink-0 items-center justify-center rounded-full border-2 border-shell-border ${className}`}
    >
      <Icon className="size-6" />
    </span>
  );
};

/**
 * Step 2: the user's saved bank, UPI and crypto accounts (the same lists the /withdraw page
 * shows). Adding one happens on the /withdraw page.
 */
const WithdrawAccountList = ({ accounts, isLoading, isError, onSelect, onAddAccount }: Props) => {
  const { t } = useTranslation();

  const addButton = (primary: boolean) => (
    <button
      type="button"
      onClick={onAddAccount}
      className={`flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold transition ${
        primary
          ? 'bg-gold text-gold-foreground hover:bg-gold-hover'
          : 'border border-dashed border-shell-border text-shell-fg hover:border-gold hover:text-gold'
      }`}
    >
      <Icons.plus className="size-4" />
      {t('withdraw.addAccount')}
    </button>
  );

  if (isLoading) {
    return (
      <p className="py-8 text-center text-sm text-shell-muted">{t('withdraw.loadingAccounts')}</p>
    );
  }
  if (isError) {
    return (
      <p className="py-8 text-center text-sm text-danger-red">{t('withdraw.accountsError')}</p>
    );
  }
  if (!accounts.length) {
    return (
      <div className="space-y-4 py-4 text-center">
        <p className="text-sm text-shell-muted">{t('withdraw.noAccounts')}</p>
        {addButton(true)}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {accounts.map((account) => (
          <li key={account.id}>
            <button
              type="button"
              onClick={() => onSelect(account)}
              className="flex min-h-[72px] w-full items-center justify-between gap-3 rounded-xl border border-transparent bg-shell-raised px-5 py-3 text-left transition hover:border-gold"
            >
              <span className="min-w-0 break-words text-base text-shell-fg">{account.label}</span>
              <WithdrawAccountIcon kind={account.kind} />
            </button>
          </li>
        ))}
      </ul>
      {addButton(false)}
    </div>
  );
};

export default WithdrawAccountList;
